import {
  AWProposalV2,
  FXRates,
  ProposablePortfolio_TEMP,
  ProposablePortfolioGroup_TEMP,
  Proposal_TEMP,
} from '@blk/aw-api-clients';
import { AggregateComposition, DynamicSleeveDef, ExternalId, Portfolio } from '@blk/aw-bff-typescript-models';
import { Position, ProposalV7 } from '@blk/awt-aladdinportfoliobuilderserver';
import { compact, flatten, isNil, uniq } from 'lodash';
import memoizeOne from 'memoize-one';
import { RenderableOutput, RenderableOutputKey } from './AnalysisUtils';
import { convertPortfolioToModelPortfolio } from './AWPortfolioUtils';
import { getFxConversionRate } from './FxUtils';
import { ProposalGeneratorInputs } from './ProposalGuideUtils';

const EXCLUDE_PARAM_KEYS = [
  'productSelection',
  'originalSleeves',
  'selectedAccount',
  'portfolioToPositionMap',
  'productFunding',
  'buyList',
  'targetSellAmount',
  'selectedCampaignEntity',
  'modelSelector',
];

export const EXCLUDE_PARAM_KEY_FLAG = 'pg-param-exclude';

type AggregateAndParent =
  | {
      parent: AggregateAndParent;
      aggregate: AggregateComposition;
    }
  | undefined;
export const searchForAggregateAndParent = (
  aggIDTag: string,
  aggIDValue: string,
  aggregate: AggregateComposition | undefined
): AggregateAndParent => {
  if (!aggregate) {
    return undefined;
  }
  if (aggIDTag === aggregate.tag && aggIDValue === aggregate.id) {
    return { parent: undefined, aggregate };
  }
  let result;
  aggregate.childAggregates.forEach((subAgg) => {
    const foundAgg = searchForAggregateAndParent(aggIDTag, aggIDValue, subAgg);
    if (foundAgg) {
      result = foundAgg;
      let nodeToAttachParent = result;
      while (nodeToAttachParent.parent) {
        nodeToAttachParent = nodeToAttachParent.parent;
      }
      nodeToAttachParent.parent = { aggregate, parent: undefined };
    }
  });
  return result;
};

export const searchForAggregate = (
  aggIDTag: string,
  aggIDValue: string,
  originalAggregate: AggregateComposition | undefined
): AggregateComposition | undefined => searchForAggregateAndParent(aggIDTag, aggIDValue, originalAggregate)?.aggregate;

export const createProposablePortfolioMap = (portfolios: ProposablePortfolio_TEMP[] | undefined) =>
  portfolios?.reduce<{
    [portfolioId: string]: ProposablePortfolio_TEMP | undefined;
  }>((acc, portfolio) => {
    acc[portfolio.id] = portfolio;
    return acc;
  }, {}) ?? {};

export const getAllNestedPortfoliosForAggregate = (aggregate: AggregateComposition | undefined): Portfolio[] => {
  const portfolios = aggregate?.portfolios.slice() ?? [];
  aggregate?.childAggregates.forEach((childAgg) => {
    portfolios.push(...getAllNestedPortfoliosForAggregate(childAgg));
  });
  return portfolios;
};

export const getAllNestedTagValueForAggregate = (aggregates: AggregateComposition[] | undefined): ExternalId[] => {
  if (!aggregates) {
    return [];
  }

  const externalIds = [];
  for (const agg of aggregates) {
    externalIds.push({ tag: agg.tag, value: agg.id }, ...getAllNestedTagValueForAggregate(agg.childAggregates));
  }

  return externalIds;
};

export const getPortfolioOrPortfolioGroupFromProposal = memoizeOne(
  (
    proposal: Proposal_TEMP,
    portfolioId: string
  ): ProposablePortfolioGroup_TEMP | ProposablePortfolio_TEMP | undefined => {
    if (proposal.portfolioGroup.id === portfolioId) {
      return proposal.portfolioGroup;
    }
    if (proposal.proposedAggregate && portfolioId.includes(':')) {
      const [aggIDTag, aggIDValue] = portfolioId.split(':');
      const aggregate = searchForAggregate(aggIDTag, aggIDValue, proposal.proposedAggregate);
      // if it's a mid-level agg, create a legacy ProposablePortfolioGroup_TEMP shape on the fly by assembling the aggregate composition
      if (aggregate) {
        const portfolios = getAllNestedPortfoliosForAggregate(aggregate);
        const proposablePortfolioMap = createProposablePortfolioMap(proposal.portfolioGroup.portfolios);
        const proposablePortfolios = portfolios.map((portfolio) => proposablePortfolioMap[portfolio.id]);
        const benchmark = convertPortfolioToModelPortfolio(aggregate.benchmark);
        const originalAggregate = searchForAggregate(aggIDTag, aggIDValue, proposal.originalAggregate);
        const originalBenchmark = originalAggregate
          ? convertPortfolioToModelPortfolio(originalAggregate.benchmark)
          : benchmark;
        const model = convertPortfolioToModelPortfolio(aggregate.model);
        const source = aggregate.origin === 'USER' ? 'CUSTOM' : proposal.portfolioGroup.source;
        return {
          id: portfolioId,
          metadata: aggregate.metadata,
          originalBenchmark,
          benchmark,
          source,
          portfolioGroupType: source === 'CUSTOM' ? undefined : aggregate.tag,
          portfolios: proposablePortfolios,
          currency: aggregate.currency,
          model,
          otherBenchmarks: aggregate.otherBenchmarks ?? [],
          otherModels: aggregate.otherModels ?? [],
        } as ProposablePortfolioGroup_TEMP;
      }
    }
    const portfolio = proposal.portfolioGroup.portfolios.find((p) => p.id === portfolioId);
    if (portfolio) {
      return portfolio;
    }
    return undefined;
  }
);

export function isProposalV7(proposal: ProposalV7 | Proposal_TEMP | AWProposalV2 | null): proposal is Proposal_TEMP {
  return proposal?.hasOwnProperty('portfolioGroup') ?? false;
}

export function filterParametersForProposalGenerator(proposalGeneratorInputs: ProposalGeneratorInputs) {
  return Object.keys(proposalGeneratorInputs).reduce((params: ProposalGeneratorInputs, key: string) => {
    if (
      !isNil(proposalGeneratorInputs[key]) &&
      !EXCLUDE_PARAM_KEYS.includes(key) &&
      !key.includes(EXCLUDE_PARAM_KEY_FLAG)
    ) {
      params[key] = proposalGeneratorInputs[key];
    }
    return params;
  }, {});
}

export type PortfolioFxConversionRates = { [portfolioId: string]: number };

export const collectPortfolioFxConversionRates = (proposal: AWProposalV2, fxRates: FXRates) => {
  const conversionRates: PortfolioFxConversionRates = {};
  proposal.originalAggregate.portfolios.forEach((portfolio) => {
    conversionRates[portfolio.id] = getFxConversionRate(portfolio.currency, proposal.currency, fxRates);
  });
  return conversionRates;
};

export const filterPositionsBySleeve = (positions: Position[], dynamicSleeveDef: DynamicSleeveDef) =>
  positions.filter((pos) => pos.posInfo[dynamicSleeveDef.tag] === dynamicSleeveDef.value);

export const getPortfolioNav = (port: Portfolio, dynamicSleeveDef?: DynamicSleeveDef) =>
  (dynamicSleeveDef ? filterPositionsBySleeve(port.positions, dynamicSleeveDef) : port.positions).reduce(
    (tot, pos) => tot + (pos?.marketValue ?? 0),
    0
  );

export const getAggregateCompositionNav = (
  aggregate: AggregateComposition,
  fxConversionRates: PortfolioFxConversionRates,
  dynamicSleeveDef?: DynamicSleeveDef
): number => {
  let totalMarketValueInBaseCurrency = 0;
  aggregate.portfolios.forEach((portfolio) => {
    const nav = getPortfolioNav(portfolio, dynamicSleeveDef);
    totalMarketValueInBaseCurrency += nav * (fxConversionRates[portfolio.id] ?? 1);
  });
  return totalMarketValueInBaseCurrency;
};

export const getRenderableOutputKey = (
  renderableOutputKey: RenderableOutputKey,
  renderableOutputs: RenderableOutput[]
): RenderableOutputKey => {
  if (renderableOutputs.find((output) => output.key === renderableOutputKey)) {
    return renderableOutputKey;
  }
  const hasOriginalData = renderableOutputs.find((output) => output.key === 'original');
  const hasProposedData = renderableOutputs.find((output) => output.key === 'proposed');
  if (hasOriginalData) {
    return 'original';
  } else if (hasProposedData) {
    return 'proposed';
  } else if (renderableOutputs.find((output) => output.key === 'model')) {
    return 'model';
  }
  return 'benchmark';
};

export type PortfolioDisplayNameMap = { [key: string]: string };
export const collectPortfolioDisplayNameMap = (proposal: AWProposalV2, aggIDTag: string | undefined) => {
  const displayNameMap: PortfolioDisplayNameMap = {};
  displayNameMap[proposal.originalAggregate.id] =
    aggIDTag && proposal.originalAggregate.metadata[aggIDTag]
      ? proposal.originalAggregate.metadata[aggIDTag]
      : proposal.originalAggregate.name || proposal.originalAggregate.id;
  proposal.originalAggregate.portfolios.forEach((portfolio) => {
    displayNameMap[portfolio.id] =
      aggIDTag && portfolio.metadata[aggIDTag] ? portfolio.metadata[aggIDTag] : portfolio.name || portfolio.id;
  });
  return displayNameMap;
};

export function getAggregateCusips(aggregate: AggregateComposition | undefined) {
  if (!aggregate) {
    return [];
  }
  const allPositions = compact(flatten(aggregate.portfolios?.map(({ positions }) => positions)));
  return allPositions.map(({ cusip }) => cusip);
}

export function getPortfolioCusips(portfolio: Portfolio | undefined) {
  return portfolio?.positions?.map(({ cusip }) => cusip) || [];
}

export function getCusipsFromListOfProposals(
  proposals: Proposal_TEMP[] | undefined,
  isAggregate: boolean,
  portfolioId?: string
): string[] {
  if (proposals?.length) {
    const allCusips = proposals.flatMap((proposal) => {
      if (isAggregate) {
        return getAggregateCusips(proposal.proposedAggregate);
      }
      return getPortfolioCusips(proposal.proposedAggregate?.portfolios?.find(({ id }) => id === portfolioId));
    });
    return compact(uniq(allCusips));
  }
  return [];
}

export function getUpdatedAggregate(
  aggregate: AggregateComposition,
  id: string | undefined,
  newPortfolios: Portfolio[],
  lastUpdatedTimestamp?: string
): AggregateComposition {
  // Helper function for DFS traversal
  function dfs(node: AggregateComposition, newPortfolios: Portfolio[]): AggregateComposition {
    const targetId = id ?? aggregate.id;
    // Check if the current node's id matches the targetId
    if (`${node.tag}:${node.id}` === targetId) {
      // Create a new node with updated portfolios
      return {
        ...node,
        portfolios: [...node.portfolios, ...newPortfolios],
        ...(lastUpdatedTimestamp && { lastUpdatedTimestamp }),
      };
    }

    // Recursively search in the child nodes and update them
    const updatedChildren = node.childAggregates.map((child) => dfs(child, newPortfolios));

    // Return a new node with updated children
    return {
      ...node,
      childAggregates: updatedChildren,
    };
  }

  // Start DFS traversal from the root aggregate
  return dfs(aggregate, newPortfolios);
}

export const getActivePortfolioCurrency = (proposal: Proposal_TEMP, activePortfolioId: string | undefined): string => {
  const { portfolioGroup: aggregate } = proposal;

  if (!activePortfolioId) {
    return aggregate.currency;
  }

  const activePortfolio = aggregate.portfolios?.find((p) => p.id === activePortfolioId);
  const result: string =
    aggregate.id === activePortfolioId || !activePortfolio ? aggregate.currency : activePortfolio.currency;

  return result;
};
